import { ChevronRight } from 'lucide-react'
import type { SuggestedRecipe } from '../../lib/recipeTypes'

type Props = {
  suggestion: SuggestedRecipe
  variant:    'featured' | 'compact'
  onClick:    (suggestion: SuggestedRecipe) => void
}

export function SuggestionCard({ suggestion, variant, onClick }: Props) {
  const expiringCount = suggestion.usesExpiring.length
  const isRescue      = expiringCount >= 3

  const bandClass = isRescue
    ? 'bg-gradient-to-r from-terracotta to-sand'
    : 'bg-sand'

  if (variant === 'compact') {
    return (
      <button
        onClick={() => onClick(suggestion)}
        className="w-full mb-[10px] overflow-hidden rounded-[18px] border border-border-sand bg-paper text-left"
      >
        <div className={`h-1 ${bandClass}`} />
        <div className="px-4 py-3 flex items-center gap-3">
          <div className="flex-1 min-w-0">
            <h3 className="font-serif text-[15px] font-medium tracking-[-0.01em] text-ink truncate">
              {suggestion.name}
            </h3>
            <p className="text-[11px] italic text-ink-muted mt-0.5">
              Uses {expiringCount} expiring · {suggestion.prepMinutes} min · serves {suggestion.serves}
            </p>
          </div>
          <ChevronRight className="h-[14px] w-[14px] text-ink-muted flex-shrink-0" strokeWidth={1.6} />
        </div>
      </button>
    )
  }

  // Featured variant (rank 1)
  const ownedShown  = suggestion.usesOnHand.slice(0, 1)
  const hiddenCount = (suggestion.usesOnHand.length - ownedShown.length) + suggestion.needsToBuy.length

  return (
    <button
      onClick={() => onClick(suggestion)}
      className="w-full mb-[10px] overflow-hidden rounded-[18px] border border-border-sand bg-paper text-left"
    >
      <div className={`h-1 ${bandClass}`} />
      <div className="px-4 py-[14px] flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <h3 className="font-serif text-[17px] font-medium tracking-[-0.01em] text-ink leading-[1.2]">
            {suggestion.name}
          </h3>
          <p className="text-[11px] italic text-ink-muted mt-0.5">
            Uses {expiringCount} expiring · {suggestion.prepMinutes} min · serves {suggestion.serves}
          </p>

          <div className="flex flex-wrap gap-1 mt-[10px]">
            {suggestion.usesExpiring.map(name => (
              <span
                key={name}
                className="inline-flex items-center gap-1 rounded-[6px] border border-[#F0E0C8] bg-[#FDF4EC] px-2 py-[3px] text-[10px] font-semibold text-terracotta"
              >
                <span className="h-[5px] w-[5px] rounded-full bg-terracotta" />
                {name}
              </span>
            ))}
            {ownedShown.map(name => (
              <span
                key={name}
                className="rounded-[6px] bg-[#E0EBE2] px-2 py-[3px] text-[10px] font-semibold text-forest"
              >
                {name}
              </span>
            ))}
            {hiddenCount > 0 && (
              <span className="rounded-[6px] bg-[#F0EBE0] px-2 py-[3px] text-[10px] font-semibold text-ink-muted">
                + {hiddenCount} more
              </span>
            )}
          </div>
        </div>
        <ChevronRight className="h-[14px] w-[14px] text-ink-muted mt-1 flex-shrink-0" strokeWidth={1.6} />
      </div>
    </button>
  )
}
